import { useCallback } from 'react';
import { useEditor, activeMasterOf } from '../context/EditorContext';
import { canEditWrapPoints, hasCustomWrapPoints, initialWrapPoints } from '../lib/textWrap';
import { toast } from 'sonner';

/** Objects on the active page / master for an arbitrary (possibly fresher-than-render) state snapshot. */
const activeObjectsOf = (s) => (s.viewMode === 'master' ? activeMasterOf(s)?.objects || [] : [...(s.pages[s.currentPageIndex]?.objects || []), ...(s.scratch?.objects || [])]);

/**
 * Publisher "Edit Wrap Points": enter / leave the editing mode and reset an object's hand-edited wrap outline.
 * Points live in `properties.wrapPoints` (unit space) so they follow moves, resizes and rotation automatically.
 * The mode is `state.wrapEditId`; the reducer clears it whenever the object stops being the single selection,
 * is deleted, gets locked or loses its points.
 */
export const useWrapPoints = () => {
  const { state, stateRef, dispatch, rawDispatch } = useEditor();

  // Always read the latest state (menu / dialog callbacks may run right after other dispatches in the same tick).
  const find = useCallback((id) => activeObjectsOf(stateRef.current).find((o) => o.id === id), [stateRef]);

  /** Start editing wrap points for `objectId` (defaults to the single selected object). */
  const enter = useCallback((objectId) => {
    const s = stateRef.current;
    const id = objectId || (s.selectedObjectIds.length === 1 ? s.selectedObjectIds[0] : null);
    const o = id ? find(id) : null;
    if (!o) { toast.info('Select a picture or shape first.'); return false; }
    if (!canEditWrapPoints(o)) { toast.info('Wrap points can be edited on pictures and shapes.'); return false; }
    if (o.locked) { toast.info('Unlock the object to edit its wrap points.'); return false; }
    const props = {};
    const ws = o.properties?.wrapStyle;
    // Wrap points only shape Tight / Through wrapping — Publisher switches to Tight when you start editing them.
    if (ws !== 'tight' && ws !== 'through') props.wrapStyle = 'tight';
    if (!hasCustomWrapPoints(o)) props.wrapPoints = initialWrapPoints(o);
    if (Object.keys(props).length) dispatch({ type: 'UPDATE_OBJECT_PROPS', payload: { ids: [o.id], props } });
    if (s.activeTool !== 'select') rawDispatch({ type: 'SET_ACTIVE_TOOL', payload: 'select' });
    if (s.editingObjectId) rawDispatch({ type: 'SET_EDITING', payload: null });
    dispatch({ type: 'SELECT_OBJECT', payload: { id: o.id } });
    rawDispatch({ type: 'SET_WRAP_EDIT', payload: o.id });
    // One-time coaching hint per browser session (the banner carries the short version).
    try {
      if (!sessionStorage.getItem('wp-wrap-points-hint')) {
        sessionStorage.setItem('wp-wrap-points-hint', '1');
        toast.info('Drag the red points to shape the wrap. Ctrl+click an edge to add a point, Ctrl+click a point to remove it. Esc or Done to finish.', { id: 'wrap-points-hint', duration: 6000 });
      }
    } catch { /* storage unavailable */ }
    return true;
  }, [stateRef, find, dispatch, rawDispatch]);

  const exit = useCallback(() => { if (stateRef.current.wrapEditId) rawDispatch({ type: 'SET_WRAP_EDIT', payload: null }); }, [stateRef, rawDispatch]);

  /** Remove custom wrap points (back to the automatic outline + wrap distance). */
  const reset = useCallback((objectIds) => {
    const s = stateRef.current;
    const ids = (objectIds && objectIds.length ? objectIds : s.selectedObjectIds).filter((id) => hasCustomWrapPoints(find(id)));
    if (!ids.length) { toast.info('These objects use their automatic wrap outline already.'); return; }
    const editingId = s.wrapEditId && ids.includes(s.wrapEditId) ? s.wrapEditId : null;
    const others = ids.filter((id) => id !== editingId);
    if (others.length) dispatch({ type: 'UPDATE_OBJECT_PROPS', payload: { ids: others, props: { wrapPoints: null } } });
    if (editingId) {
      // Stay in the mode: swap straight to fresh automatic points so the user can keep editing from the outline.
      const o = find(editingId);
      dispatch({ type: 'UPDATE_OBJECT_PROPS', payload: { ids: [editingId], props: { wrapPoints: initialWrapPoints({ ...o, properties: { ...o.properties, wrapPoints: null } }) } } });
    }
    toast.success('Wrap points reset to the automatic outline.', { id: 'wrap-points-reset' });
  }, [stateRef, find, dispatch]);

  const editing = state.wrapEditId ? activeObjectsOf(state).find((o) => o.id === state.wrapEditId) || null : null;
  return { enter, exit, reset, editing, canEdit: canEditWrapPoints, hasCustom: hasCustomWrapPoints };
};
